import { computed as computedReactive } from "vue";
import ClickOutside from "vue-click-outside";
import {
	AD_LAYOUT_STUBS,
	AD_PLACEMENTS,
	DICTIONARY_LISTS,
	GAME_ICONS_SPRITE_META_SMALL_ICONS,
	PAGE_GROUPS,
} from "@consts";
import {
	canonical,
	getSpriteBackgroundYPositions,
	flattenQueryParams,
	getSearchParamsFromRoute,
	isEmpty,
} from "@utils";
import computed from "./computed";
import methods from "./methods";
import { getServiceEndpointProvider } from "@/common/modules/service-endpoint-provider";
import { getRequestMediator } from "@/common/modules/request-mediator";

import wordListMixin from "@/plugins/mixins/word-lists";
import { shouldSetNoIndexHeader } from "~/pages/Results/helpers";

export default {
	name: "Results",
	mixins: [wordListMixin],
	watch: {
		"$route.query"(oldVal, newVal) {
			if (flattenQueryParams(oldVal) !== flattenQueryParams(newVal)) {
				// At this point, when there are new parameters
				// we check if the page parameter is present and dit not change.
				// If it is the case, we just remove the page parameter
				// and replace the current url to avoid going out of bounds.
				// Then the next request will start at page 1
				if (newVal.page && oldVal.page === newVal.page) {
					delete newVal.page;
					this.$router.replace({ query: { ...newVal } });
					return;
				}

				this.$fetch();
			}
		},
	},
	asyncData({ route, redirect }) {
		const params = getSearchParamsFromRoute(route);
		const pageParam = parseInt(params.clean?.misc?.page || 1);
		const queryParams = route.query || {};
		// If the page value given is less than 1, or if a non-numeric value is given, redirect the user to the first page.
		if (
			pageParam <= 0 ||
			(queryParams.page && isNaN(parseInt(queryParams.page)))
		) {
			const paramsCopy = { ...queryParams };
			delete paramsCopy.page;
			redirect(301, {
				path: route.path,
				query: paramsCopy,
			});
		}
	},
	async fetch() {
		const params = getSearchParamsFromRoute(this.$route);
		this.searchParams = params.clean;
		this.currentSort =
			params.clean?.misc?.order_by ||
			(this.hasScoreField ? "score" : "alpha");
		const requestMediator = getRequestMediator(
			this.$route,
			getServiceEndpointProvider(this.$axios, this.$config)
		);
		this.loading = true;
		const results = await requestMediator?.get(params.clean, {
			order_by: this.currentSort,
			dictionary:
				params.clean?.misc?.dictionary || this.dictionary.value || "US",
			page: params.clean?.misc?.page || 1,
		});
		let wordGroups = results?.data?._groups || [];
		let anagramGroup = [];
		if (this.currentPageGroup === PAGE_GROUPS.ANAGRAM_PAGE) {
			const userQuery = this.$route.params.tiles;
			wordGroups = results.data._groups.filter(
				(group) => group.word_length !== userQuery.length
			);
			anagramGroup = results.data._groups.filter(
				(group) => group.word_length === userQuery.length
			);
		}
		if (!isEmpty(anagramGroup)) {
			anagramGroup = anagramGroup.map((group) => ({
				...group,
				_items: group._items.sort((a, b) =>
					a.word.localeCompare(b.word, "en", { sensitivity: "base" })
				),
			}));
		}
		const totalResultsCount = results?.data?._meta?.total || 0;
		const totalAlternativeResultsCount =
			results?.data?._augment?._meta?.total || 0;
		this.$store.commit("query/saveSearch", {
			dictionary: this.dictionary,
			results: this.fortmattedResults(
				requestMediator?.formatResults(wordGroups)
			),
			anagrams: this.fortmattedResults(
				requestMediator?.formatResults(anagramGroup)
			),
			alternativeResults: this.fortmattedResults(
				requestMediator?.formatResults(
					results?.data?._augment?._groups || []
				)
			),
			alternativeTiles:
				results?.data?._augment?._meta.augment_tiles || "",
			interlinks: results?.data?._meta?.interlinks || {},
			suggestedLinks: results?.data?._meta?.links || {},
			match: results?.data?._exact_match || {},
			totalResultsCount,
			totalAlternativeResultsCount,
		});
		this.loading = false;
		const pageParam = parseInt(params.clean?.misc?.page || 1);
		const totalPages = Math.ceil(this.totalResultsCount / 100);
		const queryParams = this.$route.query;
		// If the page value is greater than the max number of pages, redirect the user to the last page.
		if (totalPages && pageParam > totalPages) {
			return this.$nuxt.context.redirect(301, {
				path: this.$router.path,
				query: {
					...queryParams,
					page: totalPages,
				},
			});
		}
		// Set status code to 404 when we have no results. We don't take into account the augmented results
		if (
			this.$isServer &&
			totalResultsCount === 0 &&
			!this.$store.getters["navigation/isScrabbleDictionaryResultsPage"]
		) {
			this.$ssrContext.res.statusCode = 404;
		}
		// Set no index header for special cases
		if (
			this.$isServer &&
			shouldSetNoIndexHeader({
				route: this.$route,
				totalResultsCount,
			})
		) {
			this.$ssrContext?.res.setHeader("x-robots-tag", "noindex");
		}
	},
	head() {
		return {
			link: [canonical(this.$route)],
		};
	},
	data() {
		return {
			searchParams: {},
			currentSort: "score",
			mobileBoxControlsMode: null,
			showDirectFinder: false,
			AD_LAYOUT_STUBS,
			AD_PLACEMENTS,
			loading: false,
			dictSpriteCoord: getSpriteBackgroundYPositions(
				DICTIONARY_LISTS.DEFAULT,
				GAME_ICONS_SPRITE_META_SMALL_ICONS
			),
			gameSelectIsOpen: false,
			openAdvancedSearchHelpWindows: 0,
		};
	},
	provide() {
		return {
			searchParams: computedReactive(() => this.searchParams || {}),
			loading: computedReactive(() => this.loading || false),
			/**
			 * Lets the advanced search fields, wherever they sit in the sidebar's
			 * tree, tell the page that a help window is open — see the column
			 * stacking comment in the template.
			 *
			 * Counted rather than a boolean: clicking one field's help button
			 * closes another's window, and that close can arrive after the open,
			 * which would leave a flag stuck at false with a window on screen.
			 */
			onAdvancedSearchHelpToggle: (isOpen) => {
				this.openAdvancedSearchHelpWindows = Math.max(
					0,
					this.openAdvancedSearchHelpWindows + (isOpen ? 1 : -1)
				);
			},
		};
	},
	computed,
	methods,
	directives: {
		ClickOutside,
	},
};
